'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import Link from 'next/link';
import type { Plant } from '@/data/plants';
import {
  getCurrentGardenLocation,
  getCurrentZone,
  getCurrentZoneZip,
  getGardenLocationFromZip,
  saveZone,
} from '@/data/zones';
import { authWrite, getToken } from '@/lib/auth';
import { useAuthGate } from '@/context/AuthGateContext';
import { useUserData } from '@/context/UserDataContext';
import type { UserGardenLocation } from '@/lib/userGardenLocation';
import { createMissingPlantProfile } from '@/lib/missingPlantProfile';
import { formatGeneratedProfileError } from '@/lib/generatedProfiles';
import {
  buildPlantDetailHref,
  createPlantCatalogFilterContext,
  filterAndSortCanonicalPlantCatalog,
  getPlantCatalogSearchRelevance,
  getPlantSourceLabel,
  listCanonicalPlantCatalog,
  mergeCanonicalPlants,
  plantMatchesCatalogSearch,
  searchCanonicalPlantCatalog,
  type PlantCatalogFilterContext,
  type PlantCatalogTraitFilter,
  type PlantCatalogTypeFilter,
} from '@/lib/plantCatalog';
import type { CanonicalPlant } from '@/lib/normalizePlant';
import MissingPlantProfileAction, {
  canOfferPlantProfileGeneration,
  countPlantProfileNameMatches,
} from '@/components/MissingPlantProfileAction';

// ── Type filter config ────────────────────────────────────────────────────────

type CategoryFilter = PlantCatalogTypeFilter;

const TRAIT_TYPE_FILTERS: { value: PlantCatalogTraitFilter; label: string }[] = [
  { value: 'tree',       label: 'Trees'       },
  { value: 'cover-crop', label: 'Cover Crops' },
  { value: 'grass',      label: 'Grasses'     },
  { value: 'grain',      label: 'Grains'      },
];

const BASE_CATEGORY_FILTERS: { value: CategoryFilter; label: string }[] = [
  { value: 'all',       label: 'All'        },
  { value: 'vegetable', label: 'Vegetables' },
  { value: 'herb',      label: 'Herbs'      },
  { value: 'fruit',     label: 'Fruits'     },
  { value: 'flower',    label: 'Flowers'    },
];

function categoryFromNavigationContext(context: PlantCatalogFilterContext | null | undefined): CategoryFilter {
  if (!context) return 'all';
  if (context.nativeFilter) return 'native-to-me';
  if (context.zoneFilter) return 'my-zone';
  return context.category;
}

function traitFiltersFromNavigationContext(context: PlantCatalogFilterContext | null | undefined): Set<PlantCatalogTraitFilter> {
  const active = new Set<PlantCatalogTraitFilter>();
  if (!context) return active;
  if (context.treeFilter) active.add('tree');
  if (context.coverCropFilter) active.add('cover-crop');
  if (context.grassFilter) active.add('grass');
  if (context.grainFilter) active.add('grain');
  return active;
}

const TYPE_BADGE: Record<string, string> = {
  vegetable: 'bg-[#2D5016] text-white',
  herb:      'bg-[#7C5CBF] text-white',
  fruit:     'bg-[#C2703E] text-white',
  flower:    'bg-[#B85C8A] text-white',
  tree:      'bg-[#8B6F47] text-white',
  shrub:     'bg-[#6B8F6E] text-white',
  vine:      'bg-[#6B8F6E] text-white',
  other:     'bg-[#6B8F6E] text-white',
};

const TYPE_LABEL: Record<string, string> = {
  vegetable: 'Vegetable',
  herb:      'Herb',
  fruit:     'Fruit',
  flower:    'Flower',
  tree:      'Tree',
  shrub:     'Shrub',
  vine:      'Vine',
  other:     'Other',
};

const SUNLIGHT_LABEL: Record<Plant['sunlight'], string> = {
  'full-sun':      'Full Sun',
  'partial-shade': 'Part Shade',
  'full-shade':    'Full Shade',
  'full-sun-to-partial-shade': 'Sun/Part Shade',
};

const WATER_LABEL: Record<Plant['water'], string> = {
  low:      'Low',
  moderate: 'Moderate',
  high:     'High',
  'low-to-moderate': 'Low/Mod',
};

// Trim days-to-maturity to fit stat chip
function shortMaturity(raw: string): string {
  return raw.split(/[;,]/)[0].trim();
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round">
      <circle cx="6.5" cy="6.5" r="4.5" />
      <path d="M10.5 10.5L14 14" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round">
      <path d="M1.5 1.5L11.5 11.5M11.5 1.5L1.5 11.5" />
    </svg>
  );
}

function EmptyLeafIcon() {
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 40V22" />
      <path d="M22 22C18 22 9 20 9 11C9 4.5 15 2.5 19 6C21 8 22 15 22 22Z" />
      <path d="M22 31C26 31 35 29 35 20C35 13.5 29 11.5 25 15C23 17 22 24 22 31Z" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round">
      <circle cx="6.5" cy="6.5" r="2.25" />
      <path d="M6.5 1v1M6.5 11v1M1 6.5h1M11 6.5h1
               M2.7 2.7l.7.7M9.6 9.6l.7.7
               M9.6 3.4l-.7.7M3.4 9.6l-.7.7" />
    </svg>
  );
}

function WaterIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6.5 1.5L10 7.5C10 9.98 8.43 12 6.5 12C4.57 12 3 9.98 3 7.5L6.5 1.5Z" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round">
      <circle cx="6.5" cy="6.5" r="5" />
      <path d="M6.5 3.5V6.5L8.5 8.5" />
    </svg>
  );
}

function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none" strokeWidth="1.5"
      strokeLinecap="round" strokeLinejoin="round"
      stroke={filled ? '#8B6F47' : '#C8C0B5'}>
      <path
        d="M9 15.5S2 11 2 5.5A3.5 3.5 0 0 1 8.5 4L9 4.5L9.5 4A3.5 3.5 0 0 1 16 5.5C16 11 9 15.5 9 15.5Z"
        fill={filled ? '#8B6F47' : 'none'}
      />
    </svg>
  );
}

// ── Plant Card ────────────────────────────────────────────────────────────────

function PlantCard({ plant, userZone, navigationContext, isFavorited, onToggleFavorite, showBestMatch }: {
  plant: CanonicalPlant;
  userZone: number | null;
  navigationContext: PlantCatalogFilterContext;
  isFavorited: boolean;
  onToggleFavorite: (id: string) => void;
  showBestMatch?: boolean;
}) {
  const zoneMatch = userZone !== null ? plant.zones.includes(userZone) : null;
  const sourceLabel = getPlantSourceLabel(plant);
  const href = buildPlantDetailHref(plant.id, navigationContext);

  return (
    <Link href={href} className="group block h-full">
      <article
        className="
          relative h-full flex flex-col
          bg-white rounded-xl border border-sand
          p-5 gap-3
          transition-all duration-200
          group-hover:-translate-y-0.5
          group-hover:border-sage
          group-hover:shadow-sm
        "
        >
        {/* Favorite button */}
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFavorite(plant.id); }}
          aria-label={isFavorited ? `Remove ${plant.name} from favorites` : `Save ${plant.name} to favorites`}
          className="absolute top-[14px] left-4 p-0.5 transition-transform duration-150 hover:scale-110"
        >
          <HeartIcon filled={isFavorited} />
        </button>

        {/* Community label */}
        <div className="absolute top-4 left-10 flex flex-wrap gap-1.5">
          {sourceLabel && (
            <span className="text-[11px] font-body font-semibold px-2 py-[3px] rounded-full bg-light-sage text-sage">
              {sourceLabel}
            </span>
          )}
          {showBestMatch && (
            <span className="text-[11px] font-body font-semibold px-2 py-[3px] rounded-full bg-[#FFF4D6] text-[#8A6A16]">
              Best match
            </span>
          )}
        </div>

        {/* Type badge */}
        <span
          className={`
            absolute top-4 right-4
            text-[11px] font-body font-bold
            px-2 py-[3px] rounded-full
            uppercase tracking-wide
            ${TYPE_BADGE[plant.type]}
          `}
        >
          {TYPE_LABEL[plant.type]}
        </span>

        {/* Name + scientific name */}
        <div className={`pr-[72px] ${(sourceLabel || showBestMatch) ? 'px-[28px] pt-7' : 'px-[28px]'}`}>
          <h3 className="font-heading font-bold text-[18px] text-dark-text leading-snug">
            {plant.name}
          </h3>
          <p className="font-body italic text-[13px] text-sage mt-0.5">
            {plant.scientificName}
          </p>
        </div>

        {/* Description */}
        <p className="font-body text-[14px] text-[#5a5a5a] line-clamp-2 flex-1 leading-relaxed">
          {plant.description}
        </p>

        {/* Zone compatibility badge */}
        {zoneMatch !== null && (
          <span className={`self-start inline-flex items-center gap-1 font-body text-[11px]
                           font-semibold px-2 py-0.5 rounded-full
                           ${zoneMatch
                             ? 'bg-[#E8F0E4] text-[#2D5016]'
                             : 'bg-[#EEECE8] text-[#8A8678]'
                           }`}>
            {zoneMatch ? ' ✓ Grows in your zone' : 'Not ideal for your zone'}
          </span>
        )}

        {/* Stats row */}
        <div className="flex items-center gap-3 pt-3 border-t border-sand">
          {/* Sunlight */}
          <div className="flex items-center gap-1 text-sage min-w-0">
            <span className="flex-shrink-0"><SunIcon /></span>
            <span className="font-body text-[11px] whitespace-nowrap">
              {SUNLIGHT_LABEL[plant.sunlight]}
            </span>
          </div>

          {/* Water */}
          <div className="flex items-center gap-1 text-sage min-w-0">
            <span className="flex-shrink-0"><WaterIcon /></span>
            <span className="font-body text-[11px] whitespace-nowrap">
              {WATER_LABEL[plant.water]}
            </span>
          </div>

          {/* Days to maturity — pushed right, truncated */}
          <div className="flex items-center gap-1 text-sage ml-auto min-w-0 max-w-[130px]">
            <span className="flex-shrink-0"><ClockIcon /></span>
            <span className="font-body text-[11px] truncate" title={plant.daysToMaturity}>
              {shortMaturity(plant.daysToMaturity)}
            </span>
          </div>
        </div>

        {/* View details */}
        <div className="flex justify-end">
          <span className="font-body font-bold text-[13px] text-deep-green">
            View Details →
          </span>
        </div>
      </article>
    </Link>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function PlantsPage({
  initialNavigationContext = null,
}: {
  initialNavigationContext?: PlantCatalogFilterContext | null;
}) {
  const initialContextRef = useRef(initialNavigationContext);
  const [searchTerm, setSearchTerm]               = useState(initialNavigationContext?.searchQuery ?? '');
  const [debouncedSearch, setDebouncedSearch]     = useState(initialNavigationContext?.searchQuery ?? '');
  const [activeCategory, setActiveCategory]       = useState<CategoryFilter>(
    categoryFromNavigationContext(initialNavigationContext),
  );
  const [activeTraitTypeFilters, setActiveTraitTypeFilters] = useState<Set<PlantCatalogTraitFilter>>(
    traitFiltersFromNavigationContext(initialNavigationContext),
  );
  const [userZone, setUserZone]               = useState<number | null>(initialNavigationContext?.userZone ?? null);
  const [gardenLocation, setGardenLocation]   = useState<UserGardenLocation | null>(
    initialNavigationContext?.gardenLocation ?? null,
  );
  const [catalogPlants, setCatalogPlants]     = useState<CanonicalPlant[]>([]);
  const [isGenerating, setIsGenerating]       = useState(false);
  const [generationMessage, setGenerationMessage] = useState('');
  const [generationError, setGenerationError] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchDropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { triggerAuthGate } = useAuthGate();
  const { favorites, saveFavorites } = useUserData();

  // Load zone from localStorage on mount; fetch community plants
  useEffect(() => {
    const initialContext = initialContextRef.current;
    const savedZone = getCurrentZone();
    const savedGardenLocation = getCurrentGardenLocation();
    const initialZone = initialContext?.userZone ?? null;
    const initialGardenLocation = initialContext?.gardenLocation ?? null;
    const currentZone = initialZone ?? savedZone;
    const currentGardenLocation = initialGardenLocation ?? savedGardenLocation;

    setUserZone(currentZone);
    setGardenLocation(currentGardenLocation);
    void listCanonicalPlantCatalog({ includeDraftGenerated: true }).then(setCatalogPlants);

    if (!currentGardenLocation && currentZone) {
      const zip = getCurrentZoneZip();
      if (zip) {
        void getGardenLocationFromZip(zip).then(location => {
          if (!location) return;
          setGardenLocation(location);
          saveZone(currentZone, zip, location);

          if (getToken()) {
            void authWrite(
              '/api/user/zone',
              {
                method: 'PUT',
                body: JSON.stringify({ zone: currentZone, zip, gardenLocation: location }),
              },
              'Your native plant area could not be saved.',
            ).catch(() => {});
          }
        });
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      if (searchDropdownTimeoutRef.current) clearTimeout(searchDropdownTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const search = debouncedSearch.trim();
    if (search.length < 2) return;

    let cancelled = false;
    searchCanonicalPlantCatalog(search, { includeDraftGenerated: true }).then(results => {
      if (cancelled) return;
      setCatalogPlants(current => mergeCanonicalPlants(current, results));
    });

    return () => { cancelled = true; };
  }, [debouncedSearch]);

  function handleToggleFavorite(id: string) {
    if (!getToken()) { triggerAuthGate(); return; }
    void saveFavorites(currentFavorites =>
      currentFavorites.includes(id)
        ? currentFavorites.filter(favoriteId => favoriteId !== id)
        : [...currentFavorites, id],
    );
  }

  function handleSearchFocus() {
    if (searchDropdownTimeoutRef.current) clearTimeout(searchDropdownTimeoutRef.current);
    setShowSearchDropdown(true);
  }

  function handleSearchBlur() {
    searchDropdownTimeoutRef.current = setTimeout(() => setShowSearchDropdown(false), 150);
  }

  async function handleGenerateMissingPlant() {
    const query = debouncedSearch.trim() || searchTerm.trim();
    if (!query) return;
    if (!getToken()) { triggerAuthGate(); return; }

    setIsGenerating(true);
    setGenerationMessage('');
    setGenerationError('');

    try {
      const canonicalProfile = await createMissingPlantProfile(query);
      if (!canonicalProfile) {
        setGenerationError('Generation failed during validation: A plant name is required.');
        return;
      }

      setCatalogPlants(current => mergeCanonicalPlants(current, [canonicalProfile]));
      setSearchTerm(canonicalProfile.name);
      setDebouncedSearch(canonicalProfile.name);
      setActiveCategory('all');
      setActiveTraitTypeFilters(new Set());
      setShowSearchDropdown(false);
      setGenerationMessage(`${canonicalProfile.name} community plant profile saved.`);
    } catch (error) {
      setGenerationError(formatGeneratedProfileError(
        error,
        'Generation failed. Try a more specific plant name.',
      ));
    } finally {
      setIsGenerating(false);
    }
  }

  const favoriteIds = useMemo(() => new Set(favorites), [favorites]);

  // 200ms debounce on search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 200);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const categoryFilters: { value: CategoryFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    ...(userZone ? [{ value: 'my-zone' as CategoryFilter, label: `My Zone (${userZone})` }] : []),
    ...(gardenLocation ? [{ value: 'native-to-me' as CategoryFilter, label: 'Native to My Area' }] : []),
    ...BASE_CATEGORY_FILTERS.slice(1),
  ];

  function handleToggleTraitFilter(trait: PlantCatalogTraitFilter) {
    setActiveTraitTypeFilters(prev => {
      const next = new Set(prev);
      if (next.has(trait)) next.delete(trait); else next.add(trait);
      return next;
    });
  }

  const searchMatches = useMemo(() => {
    const search = debouncedSearch.trim().toLowerCase();

    return catalogPlants.filter(plant => plantMatchesCatalogSearch(plant, search));
  }, [catalogPlants, debouncedSearch]);

  const navigationContext = useMemo(() => createPlantCatalogFilterContext({
    activeType: activeCategory,
    activeTraitTypeFilters: Array.from(activeTraitTypeFilters),
    searchQuery: debouncedSearch,
    userZone,
    gardenLocation,
  }), [activeCategory, activeTraitTypeFilters, debouncedSearch, gardenLocation, userZone]);

  const filtered = useMemo(() => {
    return filterAndSortCanonicalPlantCatalog(catalogPlants, navigationContext);
  }, [catalogPlants, navigationContext]);
  const normalizedSearch = debouncedSearch.trim();
  const bestMatchPlantId = useMemo(() => {
    const search = normalizedSearch.trim();
    if (!search || filtered.length === 0) return null;
    const top = filtered[0];
    const relevance = getPlantCatalogSearchRelevance(top, search);
    return relevance.directMatch ? top.id : null;
  }, [filtered, normalizedSearch]);
  const nameMatchCount = useMemo(() => {
    const search = normalizedSearch.toLowerCase();
    if (!search) return 0;
    return countPlantProfileNameMatches(search, catalogPlants);
  }, [catalogPlants, normalizedSearch]);
  const canGenerateMissingPlant = canOfferPlantProfileGeneration(
    normalizedSearch,
    nameMatchCount,
  );
  const showMissingPlantPrompt = canGenerateMissingPlant && !isGenerating;
  const showMissingPlantSearchDropdown = showSearchDropdown && canGenerateMissingPlant;

  return (
    <div className="space-y-6">

      {/* Header */}
      <div>
        <h1 className="font-heading text-[28px] text-dark-text leading-tight">
          Plant Library
        </h1>
        <p className="font-body text-[16px] text-sage mt-1">
          Discover what grows best in your area
        </p>
        <p className="font-body text-[14px] text-sage mt-1">
          {filtered.length} {filtered.length === 1 ? 'plant' : 'plants'}
        </p>
      </div>

      {/* Search bar */}
      <div className="relative">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sage pointer-events-none">
          <SearchIcon />
        </span>
        <input
          type="text"
          value={searchTerm}
          onChange={e => {
            setSearchTerm(e.target.value);
            setGenerationMessage('');
            setGenerationError('');
            setShowSearchDropdown(true);
          }}
          onFocus={handleSearchFocus}
          onBlur={handleSearchBlur}
          placeholder="Search plants..."
          className="
            w-full pl-10 pr-10 py-3
            rounded-xl border border-sand bg-white
            font-body text-[15px] text-dark-text
            placeholder:text-[#bbb]
            outline-none focus:border-sage
            transition-colors duration-200
          "
        />
        {searchTerm && (
          <button
            onClick={() => {
              setSearchTerm('');
              setDebouncedSearch('');
              setGenerationMessage('');
              setGenerationError('');
              setShowSearchDropdown(false);
            }}
            aria-label="Clear search"
            className="
              absolute right-3.5 top-1/2 -translate-y-1/2
              text-[#aaa] hover:text-dark-text
              transition-colors duration-200
            "
          >
            <XIcon />
          </button>
        )}
        {showMissingPlantSearchDropdown && (
          <ul className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-lg border border-sand bg-white shadow-lg">
            <li>
              {isGenerating ? (
                <div className="px-4 py-3">
                  <MissingPlantProfileAction
                    query={normalizedSearch}
                    isGenerating
                    onCreate={handleGenerateMissingPlant}
                  />
                </div>
              ) : (
                <MissingPlantProfileAction
                  query={normalizedSearch}
                  isGenerating={false}
                  onCreate={() => { void handleGenerateMissingPlant(); }}
                  activation="mouseDown"
                  className="w-full px-4 py-3 text-left font-body text-[14px] italic text-deep-green transition-colors hover:bg-light-sage"
                />
              )}
            </li>
          </ul>
        )}
      </div>

      {/* Type filters */}
      <div className="flex flex-col gap-2">
        <div
          className="
            flex gap-2 overflow-x-auto pb-0.5
            [&::-webkit-scrollbar]:hidden
            [-ms-overflow-style:none]
            [scrollbar-width:none]
          "
        >
          {categoryFilters.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setActiveCategory(value)}
              className={[
                'flex-shrink-0 px-4 py-1.5 rounded-full font-body font-semibold text-[14px] transition-all duration-200',
                activeCategory === value
                  ? 'bg-deep-green text-white'
                  : 'bg-light-sage text-dark-text hover:bg-[#dce8d8]',
              ].join(' ')}
            >
              {label}
            </button>
          ))}
        </div>
        <div
          className="
            flex gap-2 overflow-x-auto pb-0.5
            [&::-webkit-scrollbar]:hidden
            [-ms-overflow-style:none]
            [scrollbar-width:none]
          "
        >
          {TRAIT_TYPE_FILTERS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => handleToggleTraitFilter(value)}
              className={[
                'flex-shrink-0 px-4 py-1.5 rounded-full font-body font-semibold text-[14px] transition-all duration-200',
                activeTraitTypeFilters.has(value)
                  ? 'bg-deep-green text-white'
                  : 'bg-light-sage text-dark-text hover:bg-[#dce8d8]',
              ].join(' ')}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid or empty state */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-sand">
          <EmptyLeafIcon />
          <p className="font-body text-[15px] text-sage mt-4 text-center leading-relaxed">
            {normalizedSearch
              ? `No plants match "${normalizedSearch}".`
              : 'No plants match your filters.'}
            <br />
            {normalizedSearch && searchMatches.length === 0
              ? 'Try a different search or create a community profile.'
              : 'Try adjusting your filters.'}
          </p>
          {showMissingPlantPrompt && (
            <MissingPlantProfileAction
              query={normalizedSearch}
              isGenerating={isGenerating}
              onCreate={() => { void handleGenerateMissingPlant(); }}
              className="mt-5 rounded-lg bg-deep-green px-4 py-2.5 font-body text-[14px] font-semibold text-white transition-opacity hover:opacity-90"
            />
          )}
          {isGenerating && canGenerateMissingPlant && (
            <MissingPlantProfileAction
              query={normalizedSearch}
              isGenerating
              onCreate={handleGenerateMissingPlant}
              className="mt-5"
            />
          )}
          {generationMessage && (
            <p className="mt-4 max-w-sm text-center font-body text-[13px] text-deep-green">
              {generationMessage}
            </p>
          )}
          {generationError && (
            <p className="mt-4 max-w-sm text-center font-body text-[13px] text-alert-red">
              {generationError}
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map(plant => (
            <PlantCard
              key={plant.id}
              plant={plant}
              userZone={userZone}
              navigationContext={navigationContext}
              isFavorited={favoriteIds.has(plant.id)}
              onToggleFavorite={handleToggleFavorite}
              showBestMatch={plant.id === bestMatchPlantId}
            />
          ))}
        </div>
      )}

    </div>
  );
}
